import { useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Gavel } from 'lucide-react'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import useUrlSearchParams from '@/hooks/useUrlSearchParams'
import SubHeaders from '@/components/Headers/SubHeaders'
import Paper from '@/components/Paper'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { generatePathDatacentral, paths } from '@/utils/path'

import { listTenderBidResults } from '../../services'
import { getBidResultColumns, getBidResultFilterToggles } from './column'

const BidResult = () => {
    const { t } = useTranslation('tenders')
    const { tenderID } = useParams()
    const tenderId = Number(tenderID)

    const { params, queryParams, setParams } = useUrlSearchParams({
        defaults: { sort_by: 'id', sort_order: 'desc' },
    })

    const { data, renderState } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.TENDER_BID_RESULT, tenderId, queryParams],
        queryFn: () =>
            listTenderBidResults(
                tenderId,
                queryParams as unknown as Record<string, unknown>
            ),
        enabled: !!tenderId,
        placeholderData: true,
    })

    const columns = useMemo(() => getBidResultColumns(t), [t])
    const filterToggles = useMemo(() => getBidResultFilterToggles(t), [t])

    const handleSortChange = useCallback(
        (sortBy: string, sortOrder: 'asc' | 'desc') =>
            setParams({ sort_by: sortBy, sort_order: sortOrder }),
        [setParams]
    )
    const handlePageChange = useCallback(
        (page: number) => setParams({ page }, false),
        [setParams]
    )
    const handlePageSizeChange = useCallback(
        (pageSize: number) => setParams({ page_size: pageSize, page: 1 }),
        [setParams]
    )

    if (!tenderId) return <div>Tender ID not found</div>

    const stateComponent = renderState()

    return (
        <div>
            <SubHeaders
                breadcrumbs={[
                    { title: t('title'), path: paths.tenders },
                    {
                        title: tenderId.toString(),
                        path: generatePathDatacentral(paths.tenderDetail, {
                            tenderId: tenderId.toString(),
                        }),
                    },
                    {
                        title: t('view_bid_result'),
                        path: paths.tenderBidResult.replace(
                            ':tenderID',
                            tenderId.toString()
                        ),
                    },
                ]}
            />

            <Paper className="mb-2 flex items-center gap-3 px-4 py-3">
                <Gavel className="h-5 w-5 text-primary" />
                <div>
                    <h1 className="text-base font-semibold">
                        {t('view_bid_result')}
                    </h1>
                </div>
            </Paper>

            <Paper>
                {stateComponent || (
                    <DataTableCustomized
                        columns={columns}
                        rows={data?.results || []}
                        rowKey={(row) => row.id}
                        size="small"
                        toolbar={{
                            settings: {
                                show: true,
                                storageKey: 'tenders-bid-result',
                                filterToggles,
                            },
                        }}
                        sorting={{
                            sortBy: params.sort_by,
                            sortOrder: params.sort_order,
                            onSortChange: handleSortChange,
                        }}
                        pagination={{
                            page: params.page,
                            pageSize: params.page_size,
                            total: data?.pagination?.total || 0,
                            onPageChange: handlePageChange,
                            onPageSizeChange: handlePageSizeChange,
                        }}
                    />
                )}
            </Paper>
        </div>
    )
}

export default BidResult
